/**
 * One result row: what it is, what it is called, where it sits (brief 06).
 *
 * A real link to the hit's route, so a middle-click or ⌘-click opens a tab the way every link
 * does; a plain click is routed in-app. The route is the server's (`SearchHit.route`), so the six
 * groups need no client-side routing table. No preview and no thumbnail — the brief forbids them.
 */

import {
  FileText,
  FolderKanban,
  Layers,
  ListChecks,
  MessageSquare,
  UserRound,
} from 'lucide-react';
import type { MouseEvent } from 'react';

import type { SearchGroupKey, SearchHit as Hit } from './api';
import styles from './Search.module.css';

const ICON: Readonly<Record<SearchGroupKey, typeof FileText>> = {
  drawing: FileText,
  task: ListChecks,
  comment: MessageSquare,
  node: Layers,
  project: FolderKanban,
  person: UserRound,
};

export interface SearchHitRowProps {
  readonly hit: Hit;
  /** The DOM id `aria-activedescendant` points at, inside the overlay's listbox. */
  readonly optionId?: string | undefined;
  readonly selected?: boolean;
  readonly onOpen: (hit: Hit) => void;
  /** The pointer came over the row — the overlay moves its selection there. */
  readonly onPoint?: (() => void) | undefined;
}

function isPlainClick(event: MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

export function SearchHitRow({ hit, optionId, selected = false, onOpen, onPoint }: SearchHitRowProps) {
  const Icon = ICON[hit.group];
  // Root first: *project › building › floor › zone*, live names (§5.6).
  const where = hit.breadcrumb.map((crumb) => crumb.name).join(' › ');

  return (
    <a
      href={hit.route}
      id={optionId}
      role={optionId === undefined ? undefined : 'option'}
      aria-selected={optionId === undefined ? undefined : selected}
      tabIndex={optionId === undefined ? undefined : -1}
      className={[styles.hit, selected ? styles.hitSelected : ''].filter(Boolean).join(' ')}
      onClick={(event) => {
        if (!isPlainClick(event)) return;
        event.preventDefault();
        onOpen(hit);
      }}
      onMouseMove={onPoint}
    >
      <Icon className={styles.hitIcon} size={16} strokeWidth={1.5} aria-hidden />
      <span className={styles.hitText}>
        <span className={styles.hitTitle}>{hit.title}</span>
        {hit.snippet === undefined || hit.snippet === null || hit.snippet === '' ? null : (
          <span className={styles.hitSnippet}>{hit.snippet}</span>
        )}
        {where === '' ? null : <span className={styles.hitWhere}>{where}</span>}
      </span>
    </a>
  );
}
